import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import Swal from "sweetalert2";
import { fetchData } from "../helper/api.interceptor";

const AddDiscount = () => {
    const navigate = useNavigate();

    const [discountName, setDiscountName] = useState("");
    const [discountPrice, setDiscountPrice] = useState("");
    const [couponCode, setCouponCode] = useState("");
    const [discountPercentage, setDiscountPercentage] = useState("");
    const [discountType, setDiscountType] = useState("fix_amount");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    const [neverExpires, setNeverExpires] = useState(true);
    const [isActive, setIsActive] = useState(true);

    const [loading, setLoading] = useState(false);

    const handleSubmit = async () => {
        if (!discountName || !startDate || !couponCode) {
            Swal.fire({
                icon: "warning",
                title: "Missing Fields",
                text: "Discount name, coupon code and start date are required.",
            });
            return;
        }

        if (!neverExpires && !endDate) {
            Swal.fire({
                icon: "warning",
                title: "Missing End Date",
                text: "Please choose an end date.",
            });
            return;
        }

        const payload = {
            discountName,
            discountPrice: Number(discountPrice),
            discountPercentage: Number(discountPercentage),
            discountType,
            couponCode,
            startDate,
            neverExpires,
            isActive,
            ...(neverExpires ? {} : { endDate })
        };

        setLoading(true);

        try {
            const result = await fetchData.post("discount/add", payload);

            if (result?.status_code === 200) {
                Swal.fire({
                    icon: "success",
                    title: "Success!",
                    text: "Discount added successfully.",
                }).then(() => {
                    navigate("/discount_details");
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: result?.message || "Failed to add discount.",
                });
            }
        } catch (error) {
            Swal.fire({
                icon: "error",
                title: "Oops...",
                text: "Something went wrong.",
            });
        }

        setLoading(false);
    };

    return (
        <div>
            {/* Breadcrumb */}
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
                <h6 className="fw-semibold mb-0">Add Discount</h6>

                <ul className="d-flex align-items-center gap-2">
                    <li className="fw-medium">
                        <Link
                            to="/discount_details"
                            className="d-flex align-items-center gap-1 hover-text-primary"
                        >
                            <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
                            Discount Details
                        </Link>
                    </li>
                    <li>-</li>
                    <li className="fw-medium">Add Discount</li>
                </ul>
            </div>

            <div className="card">
                <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <h6 className="fw-semibold mb-0">New Discount</h6>
                    <button
                        className="btn btn-outline-secondary d-flex align-items-center gap-2"
                        onClick={() => navigate(-1)}
                    >
                        <Icon icon="mdi:arrow-left" /> Back
                    </button>
                </div>

                <div className="card-body">

                    {/* Discount Name */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Discount Name</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter discount name"
                            value={discountName}
                            onChange={(e) => setDiscountName(e.target.value)}
                        />
                    </div>

                    {/* Discount Type */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Discount Type</label>
                        <select
                            className="form-select"
                            value={discountType}
                            onChange={(e) => setDiscountType(e.target.value)}
                        >
                            <option value="fix_amount">Fix Amount</option>
                            <option value="percentage">Percentage</option>
                        </select>
                    </div>

                    {/* Price & Percentage (Auto Hide/Show Based on Type) */}
                    <div className="row">

                        {/* Fix Amount → Show Price */}
                        {discountType === "fix_amount" && (
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Discount Price</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter price"
                                    value={discountPrice}
                                    onChange={(e) => setDiscountPrice(e.target.value)}
                                />
                            </div>
                        )}

                        {/* Percentage → Show Percentage */}
                        {discountType === "percentage" && (
                            <div className="col-md-6 mb-3">
                                <label className="form-label fw-semibold">Discount Percentage (%)</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Enter %"
                                    value={discountPercentage}
                                    onChange={(e) => setDiscountPercentage(e.target.value)}
                                />
                            </div>
                        )}
                    </div>
                    {/* Coupon Code */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Coupon Code</label>
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Enter coupon code (e.g. WINTER100)"
                            value={couponCode}
                            onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        />
                    </div>

                    {/* Start Date */}
                    <div className="mb-3">
                        <label className="form-label fw-semibold">Start Date</label>
                        <input
                            type="date"
                            className="form-control"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>

                    <div className="mb-3 form-check">
                        <input
                            type="checkbox"
                            className="form-check-input"
                            id="neverExpires"
                            checked={neverExpires}
                            onChange={(e) => setNeverExpires(e.target.checked)}
                        />
                        <label className="form-check-label fw-semibold" htmlFor="neverExpires">
                            Never Expires
                        </label>
                    </div>

                    {!neverExpires && (
                        <div className="mb-3">
                            <label className="form-label fw-semibold">End Date</label>
                            <input
                                type="date"
                                className="form-control"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </div>
                    )}

                    {/* Active Status */}
                    {/* <div className="mb-3 form-check">
                        <input
                            className="form-check-input"
                            type="checkbox"
                            checked={isActive}
                            onChange={() => setIsActive(!isActive)}
                        />
                        <label className="form-check-label fw-semibold">
                            Active
                        </label>
                    </div> */}

                    {/* Submit */}
                    <button
                        className="btn btn-success px-4"
                        onClick={handleSubmit}
                        disabled={loading}
                    >
                        {loading ? "Saving..." : "Save Discount"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddDiscount;
